import { userInstance } from '../utils/axiosConfig';

const UserService = {
    // ─── Méthodes sans authentification ──────────────────────────────────────

    addUser: async (user) => {
        try {
            const response = await userInstance.post('/add', user, {
                headers: { 'Content-Type': 'application/json' },
            });
            return response.data;
        } catch (error) {
            console.error('Error in addUser:', error.message || error);
            throw error;
        }
    },

    registerUser: async (user) => {
        try {
            const response = await userInstance.post('/register', user, {
                headers: { 'Content-Type': 'application/json' },
            });
            return response.data;
        } catch (error) {
            console.error('Error in registerUser:', error.message || error);
            throw error;
        }
    },

    login: async (user) => {
        try {
            const response = await userInstance.post('/login', user, {
                headers: { 'Content-Type': 'application/json' },
            });
            return response.data;
        } catch (error) {
            console.error('Error in login:', error.message || error);
            throw error;
        }
    },

    initPassword: async (initPasswordRequestDTO) => {
        try {
            const response = await userInstance.post('/initPassword', initPasswordRequestDTO, {
                headers: { 'Content-Type': 'application/json' },
            });
            return response.data;
        } catch (error) {
            console.error('Error in initPassword:', error.message || error);
            throw error;
        }
    },

    processIdentification: async (identificationRequestDTO) => {
        try {
            const response = await userInstance.post('/identification', identificationRequestDTO, {
                headers: { 'Content-Type': 'application/json' },
            });
            return response.data;
        } catch (error) {
            console.error('Error in processIdentification:', error.message || error);
            throw error;
        }
    },

    checkIdentification: async (username, language) => {
        try {
            const response = await userInstance.get(`/checkIdentification/${username}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in checkIdentification:', error.message || error);
            throw error;
        }
    },

    validateIdentification: async (userRequestDTO, validation, language) => {
        try {
            const response = await userInstance.post(
                `/identification/validate/${validation}`,
                userRequestDTO,
                { headers: { 'Content-Type': 'application/json', 'Accept-Language': language } }
            );
            return response.data;
        } catch (error) {
            console.error('Error in validateIdentification:', error.message || error);
            throw error;
        }
    },

    verifyEmail: async (token, language) => {
        try {
            const response = await userInstance.get(`/verifyEmail/${token}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in verifyEmail:', error.message || error);
            throw error;
        }
    },

    initForgotPassword: async (userRequestDTO, language) => {
        try {
            const response = await userInstance.post('/initForgotPassword', userRequestDTO, {
                headers: { 'Content-Type': 'application/json', 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in initForgotPassword:', error.message || error);
            throw error;
        }
    },

    reinitializePassword: async (reInitPasswordRequestDTO, language) => {
        try {
            const response = await userInstance.post('/reinitializePassword', reInitPasswordRequestDTO, {
                headers: { 'Content-Type': 'application/json', 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in reinitializePassword:', error.message || error);
            throw error;
        }
    },

    retrieveAndSendUsername: async (email, language) => {
        try {
            const response = await userInstance.post(
                '/retrieve-username',
                { email },
                { headers: { 'Content-Type': 'application/json', 'Accept-Language': language } }
            );
            return response.data;
        } catch (error) {
            console.error('Error in retrieveAndSendUsername:', error.message || error);
            throw error;
        }
    },

    // ─── Méthodes nécessitant authentification ────────────────────────────────

    /**
     * Récupère une page d'utilisateurs.
     * Le backend retourne un objet Page Spring :
     * { content: [], totalElements, totalPages, number, size, first, last, empty }
     * On retourne la réponse brute — le composant extrait ce dont il a besoin.
     */
    getAllUsers: async (page = 0, size = 50, language = 'fr') => {
        try {
            const response = await userInstance.get('/findall', {
                params: { page, size },
                headers: { 'Accept-Language': language },
            });
            return response.data; // Page Spring brut
        } catch (error) {
            console.error('Error fetching all users:', error.message || error);
            throw error;
        }
    },

    findById: async (id, language = 'fr') => {
        try {
            const response = await userInstance.get(`/find/${id}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findById:', error.message || error);
            throw error;
        }
    },

    getUserWithIdentification: async (id, language = 'fr') => {
        try {
            const response = await userInstance.get(`/complete/${id}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in getUserWithIdentification:', error.message || error);
            throw error;
        }
    },

    getUserIdentification: async (userId, language = 'fr') => {
        try {
            const response = await userInstance.get(`/identification/${userId}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in getUserIdentification:', error.message || error);
            throw error;
        }
    },

    findUsernameByUserId: async (userId, language = 'fr') => {
        try {
            const response = await userInstance.get(`/findUserName/${userId}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findUsernameByUserId:', error.message || error);
            throw error;
        }
    },

    findUserByUsername: async (username, language = 'fr') => {
        try {
            const response = await userInstance.get(`/findUser/${username}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findUserByUsername:', error.message || error);
            throw error;
        }
    },

    findUsersByEmailAndDates: async (email, dateDebut, dateFin, language = 'fr') => {
        try {
            const response = await userInstance.get(`/findUser/${email}/${dateDebut}/${dateFin}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findUsersByEmailAndDates:', error.message || error);
            throw error;
        }
    },

    findUsersByVariousElements: async (filters, language = 'fr') => {
        const {
            username = '',
            phone = '',
            email = '',
            firstName = '',
            lastName = '',
            dateDebut = null,
            dateFin = null,
            valide = null,
        } = filters;
        try {
            const response = await userInstance.get(
                `/findUser/${username}/${phone}/${email}/${firstName}/${lastName}/${dateDebut}/${dateFin}/${valide}`,
                { headers: { 'Accept-Language': language } }
            );
            return response.data;
        } catch (error) {
            console.error('Error in findUsersByVariousElements:', error.message || error);
            throw error;
        }
    },

    findIdentificationByUsername: async (username, language) => {
        try {
            const response = await userInstance.get(`/find/identification/${username}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in findIdentificationByUsername:', error.message || error);
            throw error;
        }
    },

    updateUser: async (user, language = 'fr') => {
        try {
            const response = await userInstance.put('/update', user, {
                headers: { 'Content-Type': 'application/json', 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in updateUser:', error.message || error);
            throw error;
        }
    },

    deleteUser: async (id, language = 'fr') => {
        try {
            const response = await userInstance.delete(`/delete/${id}`, {
                headers: { 'Accept-Language': language },
            });
            return response.data;
        } catch (error) {
            console.error('Error in deleteUser:', error.message || error);
            throw error;
        }
    },

    // ─── Méthodes utilitaires ─────────────────────────────────────────────────

    getCurrentUser: () => {
        try {
            return JSON.parse(localStorage.getItem('user')) || null;
        } catch (error) {
            console.error('Error retrieving current user:', error);
            return null;
        }
    },

    isAuthenticated: () => {
        const user = UserService.getCurrentUser();
        return !!(user && user.token);
    },

    logout: () => {
        try {
            localStorage.removeItem('user');
            return true;
        } catch (error) {
            console.error('Error during logout:', error);
            return false;
        }
    },
};

export default UserService;